import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, AlertTriangle, CheckCircle2, ListChecks, Send, MessageSquare, Trash2, User } from "lucide-react";
import { advisorApi, API } from "@/lib/api";
import { PageHeader, SectionCard, EmptyState, AddButton, SeverityBadge } from "@/components/shared";
import { ScoreDial } from "@/components/Charts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate } from "@/lib/finance";
import { toast } from "sonner";

export default function Advisor() {
  const [tab, setTab] = useState("insights");
  const [report, setReport] = useState(null);
  const [ranAt, setRanAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    advisorApi.latest().then((d) => { setReport(d.report); setRanAt(d.created_at); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await advisorApi.run();
      setReport(r); setRanAt(new Date().toISOString());
      toast.success("Advisor analysis complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advisor is unavailable right now");
    } finally {
      setRunning(false);
    }
  };

  const strengths = (report?.signals || []).filter((s) => s.type === "strength");
  const risks = (report?.signals || []).filter((s) => s.type === "risk");

  return (
    <div>
      <PageHeader title="AI wealth advisor" subtitle="AI-generated insights and a chat that knows your numbers." testid="advisor-header"
        actions={tab === "insights" ? <AddButton onClick={run} testid="run-advisor-button">{running ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />Analyzing…</> : <><Sparkles className="h-4 w-4 mr-1.5" />{report ? "Re-run analysis" : "Run analysis"}</>}</AddButton> : null} />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-[var(--surface-3)]">
          <TabsTrigger value="insights" className="data-[state=active]:bg-white data-[state=active]:text-[var(--green)]" data-testid="tab-insights"><Sparkles className="h-4 w-4 mr-1.5" />Insights</TabsTrigger>
          <TabsTrigger value="chat" className="data-[state=active]:bg-white data-[state=active]:text-[var(--green)]" data-testid="tab-chat"><MessageSquare className="h-4 w-4 mr-1.5" />Chat</TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="mt-4">
          {loading ? <Skeleton className="h-96 rounded-[14px]" /> : !report ? (
            <SectionCard><EmptyState icon={Sparkles} title="No analysis yet" message="Run the AI advisor to get personalized strengths, risks, and next actions based on your data." action={<AddButton onClick={run}><Sparkles className="h-4 w-4 mr-1.5" />Run analysis</AddButton>} /></SectionCard>
          ) : running ? (
            <div className="space-y-4"><Skeleton className="h-40 rounded-[14px]" /><Skeleton className="h-64 rounded-[14px]" /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4 space-y-4">
                <SectionCard title="Financial health" testid="health-card">
                  <div className="flex flex-col items-center py-2">
                    <ScoreDial value={report.health_score} label="Health" size={180} />
                    {ranAt && <p className="text-[11px] text-[var(--text-faint)] mt-2">Analyzed {fmtDate(ranAt)}</p>}
                  </div>
                </SectionCard>
                <SectionCard title="Top actions" testid="top-actions-card">
                  <ol className="space-y-2">
                    {(report.top_actions || []).map((a, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-[var(--text)]" data-testid={`top-action-${i}`}>
                        <span className="grid place-items-center h-5 w-5 rounded-full bg-[color:rgba(198,139,60,0.16)] text-[var(--gold)] text-[11px] font-semibold shrink-0 mt-0.5">{i + 1}</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ol>
                </SectionCard>
              </div>
              <div className="lg:col-span-8 space-y-4">
                <SectionCard testid="summary-card">
                  <div className="flex items-start gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)] shrink-0"><Sparkles className="h-5 w-5" /></span>
                    <div><h3 className="font-serif-display text-lg text-[var(--text)] mb-1">Advisor summary</h3><p className="text-sm text-[var(--text-muted)] leading-relaxed">{report.summary}</p></div>
                  </div>
                </SectionCard>
                {risks.length > 0 && (
                  <SectionCard title="Risks & opportunities" subtitle={`${risks.length} flagged`} testid="risks-card">
                    <div className="space-y-3">{risks.map((s, i) => <SignalRow key={i} s={s} icon={AlertTriangle} testid={`risk-signal-${i}`} />)}</div>
                  </SectionCard>
                )}
                {strengths.length > 0 && (
                  <SectionCard title="Strengths" subtitle={`${strengths.length} identified`} testid="strengths-card">
                    <div className="space-y-3">{strengths.map((s, i) => <SignalRow key={i} s={s} icon={CheckCircle2} testid={`strength-signal-${i}`} />)}</div>
                  </SectionCard>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="chat" className="mt-4">
          <ChatPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const SUGGESTIONS = [
  "How can I improve my savings rate this month?",
  "What should I prioritize: paying debt or investing?",
  "Am I on track to hit my goals?",
  "Where am I overspending?",
];

function ChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/advisor/chat/history`, { headers: authHeader() })
      .then((r) => r.json()).then((d) => setMessages(d.messages || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            toast.error("Advisor error: " + payload.error);
          }
        }
      }
    } catch (e) {
      toast.error("Could not reach the advisor");
      setMessages((m) => {
        const copy = [...m];
        if (copy.length && copy[copy.length - 1].role === "assistant" && !copy[copy.length - 1].content)
          copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    try { await fetch(`${API}/advisor/chat/history`, { method: "DELETE", headers: authHeader() }); setMessages([]); toast.success("Chat cleared"); }
    catch { toast.error("Could not clear chat"); }
  };

  return (
    <SectionCard title="Chat with your advisor" right={messages.length ? <Button variant="ghost" size="sm" onClick={clearChat} className="text-[var(--text-muted)]" data-testid="clear-chat-button"><Trash2 className="h-3.5 w-3.5 mr-1" />Clear</Button> : null} testid="chat-card" contentClass="flex flex-col">
      <div ref={scrollRef} className="h-[52vh] overflow-auto space-y-4 pr-1" data-testid="chat-messages">
        {loading ? <Skeleton className="h-24 rounded-[12px]" /> : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)] mb-4"><MessageSquare className="h-6 w-6" /></span>
            <p className="font-serif-display text-lg text-[var(--text)]">Ask me anything about your money</p>
            <p className="text-sm text-[var(--text-muted)] mt-1 max-w-sm">I can see your accounts, goals, budgets, and credit. Try one of these:</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}
                  className="text-sm rounded-full border border-[color:var(--border-subtle)] bg-white px-3 py-1.5 text-[var(--text)] hover:border-[color:rgba(31,115,85,0.4)] hover:bg-[color:rgba(31,115,85,0.06)] transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} streaming={streaming && i === messages.length - 1 && m.role === "assistant"} />)
        )}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 mt-4 pt-4 border-t border-[color:var(--border-subtle)]">
        <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={streaming}
          placeholder="Ask about your savings, debt, goals…" className="bg-white border-[color:var(--border-subtle)]" data-testid="chat-input" />
        <Button type="submit" disabled={streaming || !input.trim()} className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="chat-send-button">
          {streaming ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </SectionCard>
  );
}

const Bubble = ({ role, content, streaming }) => {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
      <span className={`grid place-items-center h-8 w-8 rounded-full shrink-0 ${isUser ? "bg-[color:rgba(23,35,31,0.08)] text-[var(--text-muted)]" : "bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)]"}`}>
        {isUser ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </span>
      <div className={`max-w-[80%] rounded-[14px] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-[var(--green)] text-[#f7fbf9]" : "bg-white border border-[color:var(--border-subtle)] text-[var(--text)]"}`}>
        {content || (streaming ? <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span> : "")}
      </div>
    </div>
  );
};

const Dot = ({ d = 0 }) => (
  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--text-faint)] animate-bounce" style={{ animationDelay: `${d}s` }} />
);

const authHeader = () => {
  const token = localStorage.getItem("wcc_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SignalRow = ({ s, icon: Icon, testid }) => (
  <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-4" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2.5">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${s.type === "strength" ? "text-[var(--green)]" : "text-[var(--gold)]"}`} />
        <div><div className="text-sm font-semibold text-[var(--text)]">{s.title}</div><p className="text-sm text-[var(--text-muted)] mt-0.5">{s.why}</p></div>
      </div>
      <SeverityBadge type={s.type} severity={s.severity} />
    </div>
    <div className="mt-3 pl-6 flex items-start gap-2 text-sm">
      <ListChecks className="h-4 w-4 text-[var(--green)] mt-0.5 shrink-0" />
      <span className="text-[var(--text)]">{s.recommendation}</span>
    </div>
  </div>
);
